/**
 * What the building is made of, and what a body can and cannot walk through.
 *
 * Everything solid in the hotel is an axis-aligned box. That is not a
 * simplification forced by the greybox rule, it is what a hotel *is* at
 * blockout scale: walls, door headers, dock platforms, crates. Restricting the
 * world to AABBs buys an exact, cheap, jitter-free collision response, and it
 * means the same data that draws a wall is the data that stops you.
 *
 * The body is a vertical capsule collapsed to a circle in XZ plus a foot and
 * head height. A real capsule test would only differ on the top and bottom
 * caps, which in a building you never touch: you walk into walls at chest
 * height. What the caps *would* have bought — smoothly stepping over a low
 * ledge — is bought here more directly by simply ignoring boxes short enough to
 * step onto.
 *
 * Pure, and deliberately not three.js. `src/world/` builds the geometry that
 * agrees with this data; this module never sees a mesh.
 */

export class LevelConfigError extends Error {}

/** How high a ledge can be before it is a wall instead of a step. Metres. */
export const STEP_HEIGHT = 0.28;

/** A hair of slack, so resting exactly on a contact plane is not a coin toss. */
const CLEARANCE = 1e-4;

/** Throws on level data that cannot be collided against, so a typo fails at boot. */
export function validateBoxes(boxes) {
  if (!Array.isArray(boxes)) throw new LevelConfigError('solids must be an array');
  for (const box of boxes) {
    const { id, min, max } = box;
    if (!id) throw new LevelConfigError('a solid has no id');
    for (const axis of ['x', 'y', 'z']) {
      if (!Number.isFinite(min?.[axis]) || !Number.isFinite(max?.[axis])) {
        throw new LevelConfigError(`solid "${id}" has a bad ${axis}`);
      }
      if (max[axis] < min[axis]) {
        throw new LevelConfigError(`solid "${id}" is inside out on ${axis}`);
      }
    }
  }
  return boxes;
}

/**
 * A uniform grid over the XZ plane, so a collision query touches the dozen
 * boxes near the player instead of the eight hundred in the building.
 *
 * Uniform rather than a tree because the property is a fixed, flat, known size
 * and the boxes never move. A grid over that is two multiplications and a map
 * lookup, and it rebuilds in a millisecond if the level data ever does change.
 */
export function createBroadphase(boxes, { cellSize = 4 } = {}) {
  let cells = new Map();
  let contents = [];
  const key = (cx, cz) => `${cx},${cz}`;

  /**
   * Fills the grid from a box list, in place.
   *
   * In place, and not by handing back a new broadphase, because everything that
   * collides — the character, the cart, every NPC, the camera — is given this
   * object once at boot and holds it forever. A door that can be chained shut
   * has to change the building those holders are colliding against, and the
   * alternative is threading a getter through five constructors so that all of
   * them can keep asking whether the world has been replaced yet.
   */
  function rebuild(nextBoxes) {
    validateBoxes(nextBoxes);
    contents = nextBoxes;
    cells = new Map();
    for (const box of nextBoxes) {
      const x0 = Math.floor(box.min.x / cellSize);
      const x1 = Math.floor(box.max.x / cellSize);
      const z0 = Math.floor(box.min.z / cellSize);
      const z1 = Math.floor(box.max.z / cellSize);
      for (let cx = x0; cx <= x1; cx += 1) {
        for (let cz = z0; cz <= z1; cz += 1) {
          const k = key(cx, cz);
          let bucket = cells.get(k);
          if (!bucket) cells.set(k, (bucket = []));
          bucket.push(box);
        }
      }
    }
  }

  rebuild(boxes);

  /** Every box whose cell overlaps the XZ rectangle. May repeat nothing. */
  function query(minX, minZ, maxX, maxZ) {
    const found = new Set();
    const x0 = Math.floor(minX / cellSize);
    const x1 = Math.floor(maxX / cellSize);
    const z0 = Math.floor(minZ / cellSize);
    const z1 = Math.floor(maxZ / cellSize);
    for (let cx = x0; cx <= x1; cx += 1) {
      for (let cz = z0; cz <= z1; cz += 1) {
        const bucket = cells.get(key(cx, cz));
        if (!bucket) continue;
        for (const box of bucket) found.add(box);
      }
    }
    return [...found];
  }

  return {
    query,
    rebuild,
    /** Everything within `radius` of a point, vertical range unfiltered. */
    near: (x, z, radius) => query(x - radius, z - radius, x + radius, z + radius),
    get boxes() {
      return contents;
    },
    cellSize,
  };
}

/**
 * Pushes a circle out of every box it overlaps.
 *
 * Every contact in a pass contributes, not just the deepest, and this is the
 * difference between a doorway being narrow and a doorway being a suggestion.
 * A body wider than the gap touches both jambs at once; taking only the deepest
 * gives a push off one jamb, then next pass a push off the other, and the two
 * cancel while the component that should have stopped the body entering is
 * thrown away half the time. A metre-wide cart walked straight through a three
 * foot service door.
 *
 * The sum is then clamped per axis to the largest single contribution on that
 * axis. Without the clamp, a body resting on the seam between two collinear
 * pieces of the same wall — which is how every wall with a door in it is
 * built — gets pushed twice as far as it should and visibly pops. With it,
 * agreeing contacts count once and disagreeing contacts still cancel.
 *
 * Iterated, because resolving one wall can push you into another, and a single
 * pass at a corner leaves you inside the jamb. Three passes is enough for any
 * corner a building has; a fourth has never fired in practice.
 *
 * `feet` and `head` decide which boxes count. A box whose top is within a step
 * of the feet is not a wall, it is a floor, and walking into it should carry
 * you up rather than stop you dead.
 */
export function resolveHorizontal(position, radius, feet, head, world, { passes = 3 } = {}) {
  let { x, z } = position;
  const contacts = [];

  for (let pass = 0; pass < passes; pass += 1) {
    let sumX = 0;
    let sumZ = 0;
    let capX = 0;
    let capZ = 0;
    let touched = false;

    for (const box of world.near(x, z, radius)) {
      if (box.max.y <= feet + STEP_HEIGHT) continue;
      if (box.min.y >= head) continue;

      const push = pushOut(x, z, radius, box);
      if (!push) continue;
      touched = true;
      if (!contacts.includes(box)) contacts.push(box);

      const dx = push.nx * (push.depth + CLEARANCE);
      const dz = push.nz * (push.depth + CLEARANCE);
      sumX += dx;
      sumZ += dz;
      capX = Math.max(capX, Math.abs(dx));
      capZ = Math.max(capZ, Math.abs(dz));
    }

    if (!touched) break;
    x += clamp(sumX, -capX, capX);
    z += clamp(sumZ, -capZ, capZ);
  }

  return { x, z, contacts };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Whether a circle is inside anything at all, after the resolver has had its go.
 *
 * The resolver is a *soft* solver: it nudges a body out over several frames,
 * which is the right feel for a person brushing a wall. It is the wrong answer
 * for a body that physically does not fit — a metre-wide cart in a three foot
 * doorway penetrates at every position in the gap, so the nudge is real but
 * tiny, and anything dragging the body along simply out-pulls it.
 *
 * Callers that need "it does not fit, full stop" resolve, ask this, and revert.
 */
export function penetrates(x, z, radius, feet, head, world) {
  for (const box of world.near(x, z, radius)) {
    if (box.max.y <= feet + STEP_HEIGHT) continue;
    if (box.min.y >= head) continue;
    if (pushOut(x, z, radius, box)) return true;
  }
  return false;
}

/** How far, and which way, to move a circle so it stops overlapping one box. */
function pushOut(x, z, radius, box) {
  const cx = Math.min(Math.max(x, box.min.x), box.max.x);
  const cz = Math.min(Math.max(z, box.min.z), box.max.z);
  const dx = x - cx;
  const dz = z - cz;
  const distance = Math.hypot(dx, dz);

  if (distance > 1e-9) {
    if (distance >= radius) return null;
    return { nx: dx / distance, nz: dz / distance, depth: radius - distance };
  }

  // Dead centre inside the box: there is no direction to the nearest surface,
  // so leave by the nearest face instead. This happens when a body is spawned
  // inside geometry, and the alternative is being stuck forever.
  const candidates = [
    { nx: -1, nz: 0, depth: x - box.min.x + radius },
    { nx: 1, nz: 0, depth: box.max.x - x + radius },
    { nx: 0, nz: -1, depth: z - box.min.z + radius },
    { nx: 0, nz: 1, depth: box.max.z - z + radius },
  ];
  return candidates.reduce((best, c) => (c.depth < best.depth ? c : best));
}

/**
 * The height of the floor under a circle: the tallest box top that is at or
 * just above the feet, or 0 for the slab.
 *
 * "Just above" is what makes a step a step. Without it, walking at a 6-inch
 * riser would either stop you or drop your feet through it, depending on which
 * test ran first.
 */
export function supportHeight(x, z, radius, feet, world) {
  let best = 0;
  for (const box of world.near(x, z, radius)) {
    if (box.max.y > feet + STEP_HEIGHT) continue;
    if (box.max.y <= best) continue;
    if (!circleOverlapsBox(x, z, radius, box)) continue;
    best = box.max.y;
  }
  return best;
}

/** The lowest box bottom above the head, or Infinity. Stops a jump at a ceiling. */
export function ceilingHeight(x, z, radius, head, world) {
  let best = Infinity;
  for (const box of world.near(x, z, radius)) {
    if (box.min.y < head) continue;
    if (box.min.y >= best) continue;
    if (!circleOverlapsBox(x, z, radius, box)) continue;
    best = box.min.y;
  }
  return best;
}

function circleOverlapsBox(x, z, radius, box) {
  const cx = Math.min(Math.max(x, box.min.x), box.max.x);
  const cz = Math.min(Math.max(z, box.min.z), box.max.z);
  return Math.hypot(x - cx, z - cz) <= radius;
}

/**
 * Whether a straight line from `a` to `b` reaches without meeting a box.
 *
 * This is what a manager's eyes are. A vision cone that does not do this sees
 * you through the storage room wall, which turns the whole write-up system from
 * a stealth problem into an unfair one.
 *
 * Slab method, in 3D: a ray meets a box only if its entry into all three slabs
 * happens before its exit from any of them.
 */
export function segmentClear(a, b, world, { ignore = () => false } = {}) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  const length = Math.hypot(dx, dy, dz);
  if (length < 1e-9) return true;

  const candidates = world.query(
    Math.min(a.x, b.x),
    Math.min(a.z, b.z),
    Math.max(a.x, b.x),
    Math.max(a.z, b.z)
  );

  for (const box of candidates) {
    if (ignore(box)) continue;
    if (raySlab(a, { x: dx, y: dy, z: dz }, box)) return false;
  }
  return true;
}

function raySlab(origin, direction, box) {
  let enter = 0;
  let exit = 1;
  for (const axis of ['x', 'y', 'z']) {
    const d = direction[axis];
    const o = origin[axis];
    if (Math.abs(d) < 1e-9) {
      if (o < box.min[axis] || o > box.max[axis]) return false;
      continue;
    }
    let t0 = (box.min[axis] - o) / d;
    let t1 = (box.max[axis] - o) / d;
    if (t0 > t1) [t0, t1] = [t1, t0];
    enter = Math.max(enter, t0);
    exit = Math.min(exit, t1);
    if (enter > exit) return false;
  }
  return true;
}

/** Whether a point sits inside an axis-aligned rectangle in XZ. */
export function inRect(x, z, rect) {
  return x >= rect.minX && x <= rect.maxX && z >= rect.minZ && z <= rect.maxZ;
}
